"use client";

import type { GamePrioritiesResponse } from "@/app/_lib/apis/gamePriorities";
import { useFlag } from "@/hooks/useFeatureFlags";
import { useHashScroll } from "@/hooks/useHashScroll";
import { useIsActivationEligible } from "@/hooks/useIsActivationEligible";
import { usePageEventOnce } from "@/hooks/usePageEventOnce";
import { Stack } from "@biom3/react";
import { RenderOnceInView } from "@ui-kit";
import dynamic from "next/dynamic";
import { SWRConfig } from "swr";
import { FeaturedQuestTakeoverBackground } from "../FeaturedQuestTakeoverBackground";
import { GemBonus } from "../GemBonusModal/GemBonus";
import { OnboardingActivation } from "../Onboarding/components/OnboardingActivation";
import { MainQuests } from "../QuestTiles/MainQuests/MainQuests";
import { OnboardingQuests } from "../QuestTiles/OnboardingQuests/OnboardingQuests";
import { PromotedSeriesQuests } from "../QuestTiles/SeriesQuests/PromotedSeriesQuests";
import { questMediaURL } from "../QuestTiles/utils";
import type { GameWithQuests } from "../QuestsV2/types";
import ParallaxContainer from "../Takeover/ParallaxContainer/ParallaxContainer";
import { QuestSearchBanner } from "../ui/custom/game-quest-search";
import {
  HeroCarousel,
  type HeroCarouselSlide,
} from "../ui/custom/hero-carousel/HeroCarousel";

const DynamicSeriesQuests = dynamic(() =>
  import("../QuestTiles/SeriesQuests/SeriesQuests").then((module) => ({
    default: module.SeriesQuests,
  })),
);

const DynamicSideQuests = dynamic(() =>
  import("../QuestTiles/SideQuests/SideQuests").then((module) => ({
    default: module.SideQuests,
  })),
);

interface DashboardProps {
  fallback?: Record<string, unknown>;
  carouselSlides?: HeroCarouselSlide[];
  gamesWithQuests?: GameWithQuests[];
  gamePriorities?: GamePrioritiesResponse | null;
}

export function Dashboard({
  fallback = {},
  carouselSlides,
  gamesWithQuests,
  gamePriorities,
}: DashboardProps) {
  usePageEventOnce({ screen: "Dashboard", userJourney: "Claim" });
  const takeoverBackgroundEnabled = useFlag("engagementFeaturedQuestThemedBg");
  const backgroundImg = "ravenquest-takeover-bg.webp";
  const globalSearchEnabled = useFlag("globalSearch");
  useHashScroll();
  const { activationEligible } = useIsActivationEligible();

  return (
    <SWRConfig value={{ fallback }}>
      <Stack
        sx={{
          pos: "relative",
          width: "100%",
          minHeight: "100%",
        }}
        testId="QuestsListPage__mainContainer"
      >
        {takeoverBackgroundEnabled && (
          <FeaturedQuestTakeoverBackground
            backgroundImg={questMediaURL(backgroundImg).toString()}
          />
        )}
        {activationEligible ? (
          <OnboardingActivation />
        ) : (
          <HeroCarousel slides={carouselSlides} />
        )}

        <ParallaxContainer>
          {/* Quest Search Banner - Above the fold */}
          {globalSearchEnabled ? (
            <QuestSearchBanner
              gamesWithQuests={gamesWithQuests ?? []}
              gamePriorities={gamePriorities ?? null}
            />
          ) : null}

          <OnboardingQuests />
        </ParallaxContainer>

        <PromotedSeriesQuests />
        <MainQuests />
        <RenderOnceInView>
          <DynamicSeriesQuests />
        </RenderOnceInView>

        <RenderOnceInView>
          <DynamicSideQuests />
        </RenderOnceInView>

        <GemBonus />
      </Stack>
    </SWRConfig>
  );
}
